import { Component, DestroyRef, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, Validators, ValidatorFn } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { Subject, filter } from 'rxjs';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { Router } from '@angular/router';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import {
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
// Shared rules: single source of truth for role 16 layout-flag resolution and
// column-suffix derivation. See role16-soci-before-tax.rules.ts for the "why" —
// the pure validator imports the same helpers so scope-driven visibility stays
// in lockstep between editor and orchestrator.
import {
  resolveRole16ColumnSuffixes,
  resolveRole16LayoutFlags,
  buildRole16FlatKey,
  Role16ColumnSuffix,
} from '../../../services/mbrs/validators/role16-soci-before-tax.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface ControlConfig {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: number;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    note?: string;
    calculation?: string;
  };
}

interface SchemaConfig {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: ControlConfig[];
}

@Component({
  selector: 'app-role16-420000-statement-of-comprehensive-income-before-tax',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    Tooltip,
    ConfirmDialog,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    ReloadPromptComponent,
    ReloadBannerComponent,
    PlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './role16-420000-statement-of-comprehensive-income-before-tax.component.html',
  styleUrls: ['./role16-420000-statement-of-comprehensive-income-before-tax.component.css']
})
export class Role16420000StatementOfComprehensiveIncomeBeforeTaxComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  schema!: SchemaConfig;
  isLoading = true;
  isConsolidated = false;
  hasPreviousYear = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Comprehensive Income (Before Tax) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax
    );
  }

  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private confirmationService = inject(ConfirmationService);
  private runtimeConfig = inject(RuntimeConfigService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  readonly fieldNoteTooltipsEnabled = this.runtimeConfig.fieldNoteTooltipsEnabled;

  private validationErrors = new Map<string, string[]>();

  private readonly sheetKey = SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax;

  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get period header for current year
   * e.g., "For the year ended 31 December 2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getPeriodHeader('current');
  }

  /**
   * Get period header for previous year
   * e.g., "For the year ended 31 December 2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getPeriodHeader('previous');
  }

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '16',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    // This sheet does not await the container's MBRS hydration, so step 1
    // has nothing to wait for and is satisfied on arrival.
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.checkConsolidatedStatus();
    this.loadSchema(); // This will call buildForm() after schema loads
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check consolidation status and comparative information from Role-2
   */
  private checkConsolidatedStatus(): void {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    // Delegate to the shared rules helper so the editor's visibility logic
    // stays bit-for-bit identical to the pure validator's
    // resolveRole16LayoutFlags() consumer.
    const flags = resolveRole16LayoutFlags(scopeOfFilingData);
    this.isConsolidated = flags.isConsolidated;
    this.hasPreviousYear = flags.hasPreviousYear;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-16 Column Configuration:', {
    //   isConsolidated: this.isConsolidated,
    //   hasPreviousYear: this.hasPreviousYear,
    //   expectedColumns: this.getColumnCount()
    // });
  }

  // Why: pure predicate, no mutation of isConsolidated / hasPreviousYear.
  // Under the reload-prompt model the component's layout MUST stay frozen
  // until the user reloads — mutating the flags here would break the
  // schema/data invariant the cached FormGroup relies on. Compare-only,
  // return boolean, let the notifier surface the prompt.
  private detectLayoutFlagsChanged(): boolean {
    const scopeOfFilingData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);
    // Same rules helper as checkConsolidatedStatus() — the notifier's job
    // is to compare the fresh slice against the cached flags, so deriving
    // both from the single shared helper keeps them aligned.
    const { isConsolidated: freshConsolidated, hasPreviousYear: freshPreviousYear } =
      resolveRole16LayoutFlags(scopeOfFilingData);

    return (
      freshConsolidated !== this.isConsolidated ||
      freshPreviousYear !== this.hasPreviousYear
    );
  }

  private router = inject(Router);

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: [SHEET_KEYS.filingPrerequisites, SHEET_KEYS.filingInfo, SHEET_KEYS.scopeOfFiling],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  /**
   * Cross-tab live adoption — when a sibling tab edits THIS sheet's own
   * statementOfComprehensiveIncomeBeforeTax slice, `applyRemoteChange`
   * rehydrates the shared state and publishes `changedSheets` on
   * `remoteUpdateApplied$`. `adoptRemoteState()` below repaints this
   * sheet's form from that state (silently, no emitted valueChanges).
   */
  private setupCrossTabLiveAdoption(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax)),
        filter(() => !!this.schema && !!this.form),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.adoptRemoteState();
      });
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(
      this.form,
      this.stateService.getSheetData(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax)
    );
    this.calculateTotals();
    this.validateAgainstApiData();
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.form);
  }

  /**
   * Get number of columns to display
   */
  getColumnCount(): number {
    if (this.isConsolidated && this.hasPreviousYear) return 4;
    if (this.isConsolidated || this.hasPreviousYear) return 2;
    return 1;
  }

  /**
   * PHASE 2: Get XBRL control name using simplified bracket notation
   * Role 16 uses "Group/Company" terminology instead of "Consolidated/Separate"
   *
   * OLD: qnameGroupCurrent
   * NEW: qname[Group][CurrentYear]
   *
   * @param qname The XBRL concept name
   * @param groupOrCompany 'Group' or 'Company'
   * @param period 'Current' or 'Previous'
   * @returns Control name in bracket format
   */
  getXbrlControlName(
    qname: string,
    groupOrCompany: 'Group' | 'Company',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${groupOrCompany}][${periodLabel}]`;
  }

  /**
   * Load schema from JSON file
   */
  private loadSchema(): void {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<SchemaConfig>(
        this.loadTraceHandle,
        'assets/forms/role-420000.form.json',
      )
      .then(data => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('Role-16 schema loaded:', this.schema);

        // Now that schema is loaded, build form and initialize
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupEditPipeline();

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.form, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });
        this.setupLayoutReactivity();
        this.setupCrossTabLiveAdoption();
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);

        // Snapshot baseline after all debounced valueChanges settle
        setTimeout(() => {
          this.stateService.markSheetInitializedWithStatus(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax, false);
        }, 600);
      })
      .catch(error => {
        console.error('Error loading schema:', error);
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading = false;
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form based on schema and column configuration
   */
  private buildForm(): void {
    const formControls: any = {};
    const validators: ValidatorFn[] = [];

    this.schema?.controls.forEach(control => {
      if (control.ui.control === 'header') {
        return; // Skip headers
      }

      if (control.ui.dualColumn) {
        // Use null for non-calculated fields, 0 for calculated/total fields
        // This ensures unmapped API data fields remain as NULL (not converted to 0)
        const defaultValue = control.ui.calculated ? 0 : null;

        // Scenario 1: Both consolidated and previous year (4 columns)
        if (this.isConsolidated && this.hasPreviousYear) {
          formControls[this.getXbrlControlName(control.qname, 'Group', 'Current')] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[this.getXbrlControlName(control.qname, 'Group', 'Previous')] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[this.getXbrlControlName(control.qname, 'Company', 'Current')] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[this.getXbrlControlName(control.qname, 'Company', 'Previous')] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
        // Scenario 2: Consolidated only (2 columns - Group/Company for current year)
        else if (this.isConsolidated && !this.hasPreviousYear) {
          formControls[this.getXbrlControlName(control.qname, 'Group', 'Current')] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[this.getXbrlControlName(control.qname, 'Company', 'Current')] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
        // Scenario 3: Previous year only (2 columns - Current/Previous for company)
        else if (!this.isConsolidated && this.hasPreviousYear) {
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
          formControls[control.qname + 'Previous'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
        // Scenario 4: Neither (1 column - Current only)
        else {
          formControls[control.qname + 'Current'] = new FormControl(
            { value: defaultValue, disabled: !!control.ui.calculated },
            validators
          );
        }
      }
    });

    this.form = new FormGroup(formControls);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-16 form built with controls:', Object.keys(formControls).length);
  }

  /**
   * Wire the single debounced edit pipeline: calculate (includes validateAgainstApiData) → persist.
   */
  private setupEditPipeline(): void {
    buildFormEditPipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      calculate: () => { this.calculateTotals(); this.validateAgainstApiData(); },
      persist: () => this.stateService.updateSheetData(
        SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
        this.form.value
      ),
    });
  }

  /**
   * Calculate all totals
   */
  private calculateTotals(): void {
    // Shared suffix matrix — identical to role11 / role14 patterns and to
    // the pure validator's column expansion. Keeping the branch ladder in
    // exactly one place means any future layout change only needs editing
    // role16-soci-before-tax.rules.ts.
    const suffixes: string[] = [
      ...resolveRole16ColumnSuffixes(this.isConsolidated, this.hasPreviousYear),
    ];

    const calculatedTotals = this.calculationService.calculateTotals_420000(
      this.form.getRawValue(),
      suffixes
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.form.get(controlName)?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
      Object.keys(calculatedTotals)
    );
  }

  /**
   * Load saved state from localStorage
   */
  private loadSavedState(): void {
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax);
    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Role-16 state loaded from localStorage');

      // Recalculate after loading
      setTimeout(() => {
        this.calculateTotals();

      }, 100);
    } else {
      // Report sheet-level status even when no saved data
      setTimeout(() => {
      }, 100);
    }
  }

  // TODO: cross-tab reactivity requires form rebuild on scopeOfFiling change — deferred

  /**
   * Auto-fill with demo data
   */
  autoFillDemo(): void {
    const demoData: any = {};

    // Demo data for each scenario
    if (this.isConsolidated && this.hasPreviousYear) {
      // 4 columns: GroupCurrent, GroupPrevious, CompanyCurrent, CompanyPrevious
      const suffixes = ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
      const currentMultiplier = [1.0, 0.9, 0.95, 0.85];

      suffixes.forEach((suffix, index) => {
        const multiplier = currentMultiplier[index];

        // Profit for the year
        demoData['ssmt:ProfitForTheYearCI' + suffix] = Math.round(8500000 * multiplier);

        // OCI - Items NOT reclassified (before tax)
        demoData['ssmt:RemeasurementOfDefinedBenefitPlansBeforeTax' + suffix] = Math.round(-200000 * multiplier);
        demoData['ssmt:FairValueChangeOnEquityInvestmentsFVOCIBeforeTax' + suffix] = Math.round(265000 * multiplier);
        demoData['ssmt:RevaluationSurplusOnPropertyPlantAndEquipmentBeforeTax' + suffix] = Math.round(665000 * multiplier);
        demoData['ssmt:ShareOfOCIOfAssociatesNotReclassifiedBeforeTax' + suffix] = Math.round(65000 * multiplier);
        demoData['ssmt:TaxRelatingToItemsNotReclassified' + suffix] = Math.round(195000 * multiplier); // Tax benefit on net

        // OCI - Items MAY BE reclassified (before tax)
        demoData['ssmt:ExchangeDifferencesOnTranslatingForeignOperationsBeforeTax' + suffix] = Math.round(-100000 * multiplier);
        demoData['ssmt:CashFlowHedgesBeforeTax' + suffix] = Math.round(133000 * multiplier);
        demoData['ssmt:FairValueChangeOnDebtInstrumentsFVOCIBeforeTax' + suffix] = Math.round(105000 * multiplier);
        demoData['ssmt:ShareOfOCIOfAssociatesMayBeReclassifiedBeforeTax' + suffix] = Math.round(40000 * multiplier);
        demoData['ssmt:TaxRelatingToItemsMayBeReclassified' + suffix] = Math.round(43000 * multiplier);

        // Attribution
        demoData['ssmt:ComprehensiveIncomeAttributableToOwnersOfParent' + suffix] = Math.round(9100000 * multiplier);
        demoData['ssmt:ComprehensiveIncomeAttributableToNonControllingInterests' + suffix] = Math.round(135000 * multiplier);
      });
    } else if (this.isConsolidated && !this.hasPreviousYear) {
      // 2 columns: GroupCurrent, CompanyCurrent
      const suffixes = ['GroupCurrent', 'CompanyCurrent'];
      const multiplier = [1.0, 0.95];

      suffixes.forEach((suffix, index) => {
        const mult = multiplier[index];

        demoData['ssmt:ProfitForTheYearCI' + suffix] = Math.round(8500000 * mult);
        demoData['ssmt:RemeasurementOfDefinedBenefitPlansBeforeTax' + suffix] = Math.round(-200000 * mult);
        demoData['ssmt:FairValueChangeOnEquityInvestmentsFVOCIBeforeTax' + suffix] = Math.round(265000 * mult);
        demoData['ssmt:RevaluationSurplusOnPropertyPlantAndEquipmentBeforeTax' + suffix] = Math.round(665000 * mult);
        demoData['ssmt:ShareOfOCIOfAssociatesNotReclassifiedBeforeTax' + suffix] = Math.round(65000 * mult);
        demoData['ssmt:TaxRelatingToItemsNotReclassified' + suffix] = Math.round(195000 * mult);
        demoData['ssmt:ExchangeDifferencesOnTranslatingForeignOperationsBeforeTax' + suffix] = Math.round(-100000 * mult);
        demoData['ssmt:CashFlowHedgesBeforeTax' + suffix] = Math.round(133000 * mult);
        demoData['ssmt:FairValueChangeOnDebtInstrumentsFVOCIBeforeTax' + suffix] = Math.round(105000 * mult);
        demoData['ssmt:ShareOfOCIOfAssociatesMayBeReclassifiedBeforeTax' + suffix] = Math.round(40000 * mult);
        demoData['ssmt:TaxRelatingToItemsMayBeReclassified' + suffix] = Math.round(43000 * mult);
        demoData['ssmt:ComprehensiveIncomeAttributableToOwnersOfParent' + suffix] = Math.round(9100000 * mult);
        demoData['ssmt:ComprehensiveIncomeAttributableToNonControllingInterests' + suffix] = Math.round(135000 * mult);
      });
    } else if (!this.isConsolidated && this.hasPreviousYear) {
      // 2 columns: Current, Previous
      const suffixes = ['Current', 'Previous'];
      const multiplier = [1.0, 0.9];

      suffixes.forEach((suffix, index) => {
        const mult = multiplier[index];

        demoData['ssmt:ProfitForTheYearCI' + suffix] = Math.round(8000000 * mult);
        demoData['ssmt:RemeasurementOfDefinedBenefitPlansBeforeTax' + suffix] = Math.round(-160000 * mult);
        demoData['ssmt:FairValueChangeOnEquityInvestmentsFVOCIBeforeTax' + suffix] = Math.round(240000 * mult);
        demoData['ssmt:RevaluationSurplusOnPropertyPlantAndEquipmentBeforeTax' + suffix] = Math.round(600000 * mult);
        demoData['ssmt:ShareOfOCIOfAssociatesNotReclassifiedBeforeTax' + suffix] = Math.round(52000 * mult);
        demoData['ssmt:TaxRelatingToItemsNotReclassified' + suffix] = Math.round(182000 * mult);
        demoData['ssmt:ExchangeDifferencesOnTranslatingForeignOperationsBeforeTax' + suffix] = Math.round(-80000 * mult);
        demoData['ssmt:CashFlowHedgesBeforeTax' + suffix] = Math.round(120000 * mult);
        demoData['ssmt:FairValueChangeOnDebtInstrumentsFVOCIBeforeTax' + suffix] = Math.round(93000 * mult);
        demoData['ssmt:ShareOfOCIOfAssociatesMayBeReclassifiedBeforeTax' + suffix] = Math.round(32000 * mult);
        demoData['ssmt:TaxRelatingToItemsMayBeReclassified' + suffix] = Math.round(40000 * mult);
        demoData['ssmt:ComprehensiveIncomeAttributableToOwnersOfParent' + suffix] = Math.round(8675000 * mult);
        demoData['ssmt:ComprehensiveIncomeAttributableToNonControllingInterests' + suffix] = 0;
      });
    } else {
      // 1 column: Current only
      demoData['ssmt:ProfitForTheYearCICurrent'] = 8000000;
      demoData['ssmt:RemeasurementOfDefinedBenefitPlansBeforeTaxCurrent'] = -160000;
      demoData['ssmt:FairValueChangeOnEquityInvestmentsFVOCIBeforeTaxCurrent'] = 240000;
      demoData['ssmt:RevaluationSurplusOnPropertyPlantAndEquipmentBeforeTaxCurrent'] = 600000;
      demoData['ssmt:ShareOfOCIOfAssociatesNotReclassifiedBeforeTaxCurrent'] = 52000;
      demoData['ssmt:TaxRelatingToItemsNotReclassifiedCurrent'] = 182000;
      demoData['ssmt:ExchangeDifferencesOnTranslatingForeignOperationsBeforeTaxCurrent'] = -80000;
      demoData['ssmt:CashFlowHedgesBeforeTaxCurrent'] = 120000;
      demoData['ssmt:FairValueChangeOnDebtInstrumentsFVOCIBeforeTaxCurrent'] = 93000;
      demoData['ssmt:ShareOfOCIOfAssociatesMayBeReclassifiedBeforeTaxCurrent'] = 32000;
      demoData['ssmt:TaxRelatingToItemsMayBeReclassifiedCurrent'] = 40000;
      demoData['ssmt:ComprehensiveIncomeAttributableToOwnersOfParentCurrent'] = 8675000;
      demoData['ssmt:ComprehensiveIncomeAttributableToNonControllingInterestsCurrent'] = 0;
    }

    this.form.patchValue(demoData);
    this.messageService.add({
      severity: 'success',
      summary: 'Demo Data Loaded',
      detail: 'Statement of Comprehensive Income (Before Tax) has been populated with sample data',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Clear all form data
   */
  clearForm(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to clear all data in this form?',
      header: 'Clear Form',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.form.reset();
        this.stateService.clearSheetData(SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax);
        this.messageService.add({
          severity: 'info',
          summary: 'Form Cleared',
          detail: 'All form data has been cleared',
          life: TOAST_DURATION_DEFAULT_MS
        });
      }
    });
  }

  /**
   * Submit form data
   */
  submitForm(): void {
    if (this.reloadPromptDismissed()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Reload required',
        detail: 'Please refresh the page to obtain the latest version of this sheet.',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS
      });
      return;
    }

    const formData = this.form.getRawValue();
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-16 Form submitted:', formData);

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Statement of Comprehensive Income (Before Tax) has been saved',
      life: TOAST_DURATION_DEFAULT_MS
    });
  }

  /**
   * Check if control is a header
   */
  isHeader(control: ControlConfig): boolean {
    return control.ui.control === 'header';
  }

  /**
   * Check if control is calculated
   */
  isCalculated(control: ControlConfig): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Check if control has dual columns
   */
  hasDualColumn(control: ControlConfig): boolean {
    return control.ui.dualColumn === true;
  }

  /**
   * Get header level class
   */
  getHeaderClass(control: ControlConfig): string {
    if (control.ui.level === 1) return 'section-header-major';
    if (control.ui.level === 2) return 'section-header-minor';
    if (control.ui.level === 3) return 'section-header-sub';
    return '';
  }

  /**
   * Get row class for styling
   */
  getRowClass(control: ControlConfig): string {
    if (this.isCalculated(control)) {
      if (control.qname.includes('Total')) return 'total-row';
      return 'calculated-row';
    }
    return '';
  }

  /**
   * Row-type guard: a monetary, dual-column line item (the rows that own an
   * Error Description cell). Mirrors the template's monetary-row gate
   * (`!isHeader(control) && hasDualColumn(control)`) and role15-320100's
   * isMonetaryItem. Used by both the template and getRowErrorDescription.
   */
  isMonetaryItem(control: ControlConfig): boolean {
    return control.ui.control === 'number' && control.ui.dualColumn === true;
  }

  /**
   * Resolve the visible columns for the current layout. Delegates to the shared
   * resolveRole16ColumnSuffixes() (the same source calculateTotals() uses), so
   * the warning surface and the calc engine stay in lockstep. `key` is the
   * Role16ColumnSuffix; `label` is the Error Description column heading.
   *
   * NOTE: `key` is NOT the literal form-control name. Role 16 uses an
   * inconsistent key format — the Group/Company branches use bracket notation
   * (`qname[Group][CurrentYear]`) while the non-consolidated branches use plain
   * suffixes (`qnameCurrent` / `qnamePrevious`). getControlName() below maps each
   * suffix to the precise form key via buildRole16FlatKey().
   */
  private visibleSuffixColumns(): { key: string; label: string }[] {
    const labels: Record<Role16ColumnSuffix, string> = {
      GroupCurrent: 'Group Current',
      GroupPrevious: 'Group Previous',
      CompanyCurrent: 'Company Current',
      CompanyPrevious: 'Company Previous',
      Current: 'Current',
      Previous: 'Previous',
    };
    return resolveRole16ColumnSuffixes(this.isConsolidated, this.hasPreviousYear).map(
      (key) => ({ key, label: labels[key] })
    );
  }

  // Developer Note (TODO): extracted-sum warning + Error Description column added, but
  // DORMANT + UNVERIFIED end-to-end — role16-420000 (Statement of Comprehensive Income,
  // Before Tax) is NOT a currently-supported/active sheet AND its N8N snapshot is not
  // seeded by filing-form-container.populateSnapshotFromMbrsData (BE work pending).
  // Re-test when the sheet is enabled.
  // (Mapping Status badge column intentionally NOT added — Error Description only.)
  //
  // CRITICAL: getControlName MUST reproduce role16's inconsistent form-key format —
  // buildForm() builds Group/Company keys via getXbrlControlName() bracket notation
  // (`qname[Group][CurrentYear]`) but non-consolidated keys via plain `qname + 'Current'`
  // / `qname + 'Previous'`. buildRole16FlatKey() encodes exactly that branch, so the
  // warning will match the right control regardless of layout.
  private validateAgainstApiData(): void {
    if (!this.schema) return;
    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
      controls: this.schema.controls,
      columns: this.visibleSuffixColumns(),
      getControlName: (qname, key) =>
        buildRole16FlatKey(qname, key as Role16ColumnSuffix),
      getCurrentValue: (name) => this.form.get(name)?.value,
    });
    this.extractedSumWarning.mergeIntoValidationErrors(
      this.validationErrors,
      this.schema.controls,
      warnings
    );
  }

  /**
   * Render the Error Description cell for a monetary row. role16 only produces
   * suggested-change warnings today; the red-error vs suggested-change split is
   * kept identical to role8/role10/role11/role15 for forward-compat (when real
   * errors land here).
   */
  getRowErrorDescription(control: ControlConfig): string {
    if (!this.isMonetaryItem(control)) return '';
    const all = this.validationErrors.get(control.qname) || [];
    const suggestedChanges = all.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
    const errors = all.filter((message) => !message.includes(EXTRACTED_SUM_MARKER));
    let result = '';
    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }
    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Reuses visibleSuffixColumns() + buildRole16FlatKey to mirror
    // validateAgainstApiData's computeMismatchWarnings call exactly.
    if (this.baselineDebug.enabled()) {
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
        control as unknown as SummableControl,
        this.visibleSuffixColumns(),
        (qname, key) => buildRole16FlatKey(qname, key as Role16ColumnSuffix)
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Does this field's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet answers the
   * question the same way as every other.
   *
   * **Wired even though this sheet declares ZERO mandatory controls today.**
   * That is deliberate, and it is the lesson from roles 9, 13 and 15: they
   * were skipped during the 2026-08-11 marker rollout for having no mandatory
   * fields, and when the PO's enumerated totals list flagged their calculated
   * totals hours later the flags landed with nowhere to render — the `*`
   * silently did not appear until smoke testing caught it. A sheet that
   * renders schema-driven labels gets the marker regardless of its current
   * mandatory count, so the next list cannot reopen that gap. The PO's enumerated totals are recorded per field in the sheet's spec under `docs/specifications/sheets/`.
   *
   * `crossSheetValue` is supplied so a cross-sheet `requiredIf` resolves.
   * Same-sheet `selfData` is passed only by sheets keyed by bare qname; the
   * per-cell sheets (`qname[Dimension][Period]`) deliberately omit it, since a
   * same-sheet lookup would silently miss.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: ControlConfig): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
